import { useState } from "react";
import type { PlanItem } from "../../utils/prepPlanner";
import type { PracticeResult } from "../../utils/prepSkills";
import { weekday } from "../../utils/prepSkills";
import { WEEKDAY_LABEL, type PrepItemState } from "../../utils/prep";
import PrepItemPopover from "./PrepItem";
import { OA_COLOR } from "./PrepOaCard";
import type { DebriefProps } from "./PrepItem";
import { cn } from "../../lib/utils";

// The week calendar: a column a day, an item a block. Blocks are coloured by
// kind, greyed once done or skipped, and can be dragged to today or a later day.

export const KIND_STYLES = {
  coding: "bg-[#ECE2D4] text-melt-text1",
  behavioral: "bg-[#DCE7DD] text-[#35503D]",
  goal: "bg-[#DCE5F0] text-[#2E4A68]",
} as const;

export const LEGEND: { label: string; style: string }[] = [
  { label: "Coding", style: KIND_STYLES.coding },
  { label: "Behavioral", style: KIND_STYLES.behavioral },
  { label: "Goals", style: KIND_STYLES.goal },
];

function blockStyle(item: PlanItem): string {
  if (item.eventId) return OA_COLOR.block;
  if (item.kind === "story") return KIND_STYLES.behavioral;
  if (item.kind === "goal") return KIND_STYLES.goal;
  return KIND_STYLES.coding;
}

const DRAG_TYPE = "application/x-caramel-prep";

export type ItemActions = {
  onDone: (item: PlanItem, day: string, result?: PracticeResult, minutes?: number) => void;
  onSkip: (item: PlanItem, day: string, back: boolean) => void;
  onUndo: (item: PlanItem) => void;
  onMove: (item: PlanItem, from: string, to: string) => void;
  /** A debrief item's form; undefined for other items */
  debriefFor: (item: PlanItem, day: string) => DebriefProps | undefined;
};

export default function PrepWeek({
  days,
  today,
  itemsOn,
  minutesOn,
  states,
  actions,
  hideTopics,
}: {
  days: string[];
  today: string;
  itemsOn: (day: string) => PlanItem[];
  minutesOn: (day: string) => number;
  states: Map<string, PrepItemState>;
  actions: ItemActions;
  /** Leave problems' topics out (the user's setting) */
  hideTopics?: boolean;
}) {
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ item: PlanItem; from: string } | null>(null);

  return (
    <div className="scrollbar-thin overflow-x-auto pb-1">
      <div className="grid min-w-[760px] grid-cols-7 gap-2">
        {days.map((day) => {
          const items = itemsOn(day);
          const isToday = day === today;
          const canDrop = day >= today && dragging !== null && dragging.from !== day;
          const date = Number(day.slice(8));
          return (
            <div
              key={day}
              onDragOver={(e) => {
                if (!canDrop) return;
                e.preventDefault();
                setDragOver(day);
              }}
              onDragLeave={() => setDragOver((d) => (d === day ? null : d))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                if (canDrop && dragging) actions.onMove(dragging.item, dragging.from, day);
                setDragging(null);
              }}
              className={cn(
                "flex min-h-[220px] flex-col gap-2 rounded-[14px] p-1 transition-colors",
                dragOver === day && "bg-melt-caramel/[.08] outline-dashed outline-1 outline-melt-caramel/50"
              )}
            >
              <div
                className={cn(
                  "flex items-center justify-between rounded-[11px] px-2.5 py-2",
                  isToday ? "bg-melt-peach" : "border border-melt-line bg-melt-paper"
                )}
              >
                <span className={cn("text-xs font-medium", isToday ? "text-melt-amber" : "text-melt-text2")}>
                  {WEEKDAY_LABEL[weekday(day)]}
                  {isToday && <span className="ml-1 font-plex text-[10px] uppercase tracking-[.06em]">today</span>}
                </span>
                <span
                  className={cn(
                    "grid h-6 min-w-6 place-items-center rounded-full px-1 text-[15px] font-semibold",
                    isToday ? "bg-melt-caramel text-white" : day < today ? "text-melt-text3" : "text-melt-text1"
                  )}
                >
                  {date}
                </span>
              </div>

              {items.length === 0 && day >= today && minutesOn(day) === 0 && (
                <div className="rounded-[11px] border border-melt-hair px-2.5 py-2 text-xs text-melt-text3">Rest day</div>
              )}

              {items.map((item) => {
                const state = states.get(item.id);
                const finished = Boolean(state?.status);
                const draggable = !finished && day >= today;
                return (
                  <PrepItemPopover
                    key={item.id}
                    item={item}
                    state={state}
                    onDone={(result, minutes) => actions.onDone(item, day, result, minutes)}
                    onSkip={(back) => actions.onSkip(item, day, back)}
                    onUndo={() => actions.onUndo(item)}
                    debrief={actions.debriefFor(item, day)}
                    hideTopics={hideTopics}
                  >
                    <button
                      type="button"
                      draggable={draggable}
                      onDragStart={(e) => {
                        e.dataTransfer.setData(DRAG_TYPE, item.id);
                        e.dataTransfer.effectAllowed = "move";
                        setDragging({ item, from: day });
                      }}
                      onDragEnd={() => {
                        setDragging(null);
                        setDragOver(null);
                      }}
                      className={cn(
                        "rounded-[11px] px-2.5 py-2 text-left transition-[opacity,transform] duration-150 hover:-translate-y-px",
                        finished ? "bg-melt-text1/[.04] text-melt-text3" : blockStyle(item),
                        draggable && "cursor-grab active:cursor-grabbing",
                        dragging?.item.id === item.id && "opacity-40"
                      )}
                    >
                      <span className={cn("block text-[12.5px] font-semibold leading-snug", (state?.status === "skipped" || state?.status === "later") && "line-through")}>
                        {item.title}
                      </span>
                      <span className="mt-1 block truncate font-plex text-[10.5px] opacity-75">
                        {[item.difficulty ?? item.goal, `${item.minutes} min`].filter(Boolean).join(" · ")}
                      </span>
                    </button>
                  </PrepItemPopover>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
